import React, { useEffect, useState, useRef } from 'react';
import {
  PopupWindow,
  Button,
  ValidationStyle,
  InputList,
  validationEngineFromInputInformationList,
  SelectInput,
  Size,
  MessagePopupCore,
} from '@utahdts/dts-react-common';
import PropTypes from 'prop-types';
import useRefLazy from '../../misc/hooks/useRefLazy';
import companyInputs from '../inputs/companyInputs';
import webservice from '../../app/webservice/webservice';
import userInputs from '../inputs/userInputs';
import roles from '../../app/roles/roles';

const propTypes = {
  company: PropTypes.object.isRequired,

  isAdmin: PropTypes.bool,

  isNew: PropTypes.bool.isRequired,

  onClose: PropTypes.func.isRequired,

  onSave: PropTypes.func.isRequired,
};

const Company = ({ company, isAdmin, isNew, onClose, onSave }) => {
  const [isBusy, setBusy] = useState(true);
  const [error, setError] = useState(undefined);
  const [users, setUsers] = useState([]);
  const [primaryContact, setPrimary] = useState({
    old: null,
    new: null,
    value: null,
  });
  const [alternativeContact, setAlternative] = useState({
    old: null,
    new: null,
    value: null,
  });
  const [inputValues, setInputValues] = useState(() =>
    Object.assign(
      {
        id: null,
        name: null,
        fein: null,
        address: null,
        address2: null,
        city: null,
        state: null,
        zip: null,
      },
      company,
    ),
  );
  const inputValuesRef = useRef();
  inputValuesRef.current = inputValues;

  const [validationEngineStyle, setValidationEngineStyle] = useState(
    ValidationStyle.BLURRED,
  );
  const validationEngineRef = useRef(undefined);

  const formatFeinInput = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    const prefix = digits.slice(0, 2);
    const suffix = digits.slice(2, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 2) return prefix;
    return `${prefix}-${suffix}`;
  };

  const inputs = useRefLazy(() =>
    companyInputs(
      {
        // Modify this callback
        inputsOnChange: (field, value) => {
          if (field === 'fein') {
            // If the field is 'fein', format the value
            const formattedFein = formatFeinInput(value);
            setInputValues((oldValues) => ({
              ...oldValues,
              [field]: formattedFein,
            }));
          } else {
            // For all other fields, update as before
            setInputValues((oldValues) => ({ ...oldValues, [field]: value }));
          }
        },
      },
      true,
      users,
    ),
  );

  const [validationEngine] = useState(() =>
    validationEngineFromInputInformationList(inputs.current),
  );
  validationEngineRef.current = validationEngine;
  if (validationEngine) {
    validationEngine.validationStyle = validationEngineStyle;
  }

  const [contactValues, setContactValues] = useState({
    name: null,
    email: null,
    phone: null,
  });
  const contactValuesRef = useRef();
  contactValuesRef.current = contactValues;

  const [contactValidationStyle, setContactValidationStyle] = useState(
    ValidationStyle.BLURRED,
  );
  const contactValidationRef = useRef(undefined);

  const contactInputs = useRefLazy(() =>
    userInputs(
      {
        inputsOnChange: (field, value) =>
          setContactValues((oldValues) => ({ ...oldValues, [field]: value })),
      },
      true,
      true,
    ),
  );

  const [contactValidation] = useState(() =>
    isNew
      ? validationEngineFromInputInformationList(contactInputs.current)
      : undefined,
  );
  contactValidationRef.current = contactValidation;
  if (contactValidation) {
    contactValidation.validationStyle = contactValidationStyle;
  }

  const [alternateValues, setAlternateValues] = useState({
    name: null,
    email: null,
    phone: null,
  });
  const alternateValuesRef = useRef();
  alternateValuesRef.current = alternateValues;

  const [alternateValidationStyle, setAlternateValidationStyle] = useState(
    ValidationStyle.BLURRED,
  );
  const alternateValidationRef = useRef(undefined);

  const alternateInputs = useRefLazy(() =>
    userInputs(
      {
        inputsOnChange: (field, value) =>
          setAlternateValues((oldValues) => ({ ...oldValues, [field]: value })),
      },
      true,
      true,
    ),
  );

  const [alternateValidation] = useState(() =>
    isNew
      ? validationEngineFromInputInformationList(alternateInputs.current)
      : undefined,
  );
  alternateValidationRef.current = alternateValidation;
  if (alternateValidation) {
    alternateValidation.validationStyle = alternateValidationStyle;
  }

  const submit = () => {
    setValidationEngineStyle(ValidationStyle.ALWAYS);
    if (isNew) {
      setContactValidationStyle(ValidationStyle.ALWAYS);
      setAlternateValidationStyle(ValidationStyle.ALWAYS);
      setContacts();
    } else {
      setCompany();
    }
  };

  const setCompany = () => {
    if (
      validationEngineRef.current &&
      validationEngineRef.current.isValid(inputValuesRef.current) &&
      primaryContact.new
    ) {
      setBusy(true);
      setError(undefined);
      onSave(
        {
          ...inputValues,
          primaryContact: primaryContact.new,
          alternativeContact: alternativeContact.new,
        },
        primaryContact.old,
        alternativeContact.old,
      )
        .then(() => {
          // Success - parent will close modal via resetCompany(true)
          // Modal will be unmounted, but set busy to false just in case
          setBusy(false);
        })
        .catch(() => {
          // Error - parent handled the error display, just reset busy state
          setBusy(false);
        });
    } else if (!primaryContact.new) {
      setError('A primary contact is required.');
    }
  };

  const setContacts = () => {
    if (
      contactValidationRef.current &&
      contactValidationRef.current.isValid(contactValuesRef.current) &&
      alternateValidationRef.current &&
      alternateValidationRef.current.isValid(alternateValuesRef.current)
    ) {
      setBusy(true);
      setError(undefined);
      webservice.company
        .submit(inputValues)
        .then((data) => {
          const org = data.submitOrg;
          const primary = setUser(contactValues, org, true);
          const alternative = setUser(alternateValues, org, false);
          // Submit sequentially (not Promise.all): if both contacts share an
          // email, the second submit must see the user the first created and
          // append its role rather than race a duplicate document, preserving
          // the unique email business key.
          return webservice.user
            .submit(primary)
            .then(() => webservice.user.submit(alternative))
            .then(() => onClose(true));
        })
        .catch(displayError);
    }
  };

  const setUser = (inputs, org, isPrimary) => {
    // Org relationships use the org FEIN (business key), never the document id
    // (see .claude/rules/03-relationship-fields.md). Prefer the FEIN echoed back
    // by submitOrg; fall back to the FEIN the admin typed on the form so a
    // create never submits an undefined org key.
    const fein = org?.fein || inputValues.fein;
    return {
      name: inputs.name,
      email: inputs.email,
      phone: inputs.phone,
      orgs: [fein],
      submitOrg: fein,
      roles: [
        {
          role: roles.orgAdmin,
          org: fein,
          primaryContact: isPrimary,
          alternativeContact: !isPrimary,
        },
      ],
    };
  };

  const formatUsers = (data) => {
    const userList = data.users || data.usersByOrg;
    const options = userList.map((user) => ({
      ...user,
      value: user.id,
      label: user.name,
    }));
    const primary = isNew
      ? undefined
      : userList.find((user) =>
          user.roles.find(
            (role) => role.primaryContact && (!isAdmin || role.org === company.fein),
          ),
        );
    const alternative = isNew
      ? undefined
      : userList.find((user) =>
          user.roles.find(
            (role) => role.alternativeContact && (!isAdmin || role.org === company.fein),
          ),
        );
    setPrimary({ old: primary, value: primary?.id, new: primary });
    setAlternative({
      old: alternative,
      value: alternative?.id,
      new: alternative,
    });
    setUsers(options);
    setBusy(false);
  };

  const displayError = () => {
    setBusy(false);
    MessagePopupCore.addMessage({
      title: 'Something went wrong',
      message: 'An error occurred. Please try again later.',
    });
  };

  useEffect(() => {
    if (isNew) {
      setBusy(false);
    } else if (isAdmin) {
      webservice.user.all().then(formatUsers).catch(displayError);
    } else {
      webservice.user.perOrg(company.fein).then(formatUsers).catch(displayError);
    }
  }, []);

  return (
    <PopupWindow
      className="popup-window--large"
      onCloseButtonCallback={() => onClose(false)}
      footerChildren={[
        <Button
          label="Close"
          onClick={() => onClose(false)}
          key="close-button"
          className="button--black"
        />,
        <Button
          label="Save"
          busy={isBusy}
          onClick={submit}
          key="save-button"
        />,
      ]}
    >
      <h3>{inputValues.id ? 'Edit Company' : 'Add New Company'}</h3>
      <h4 className="mb-spacing--small mt-zero">Company info</h4>
      <InputList
        inputs={Object.values(inputs.current)}
        data={inputValues}
        validationEngine={validationEngine}
        className="stack-form flex flex-wrap half-form"
      />
      {isNew ? (
        <section>
          <h4 className="mb-spacing--small mt-zero">Primary contact</h4>
          <InputList
            inputs={Object.values(contactInputs.current)}
            data={contactValues}
            validationEngine={contactValidation}
            className="stack-form flex flex-wrap half-form"
          />
          <h4 className="mb-spacing--small mt-zero">Alternative contact</h4>
          <InputList
            inputs={Object.values(alternateInputs.current)}
            data={alternateValues}
            validationEngine={alternateValidation}
            className="stack-form flex flex-wrap half-form"
          />
        </section>
      ) : (
        <section className="stack-form">
          <SelectInput
            field="contactPrimary"
            label="Primary Contact:"
            multiSelect={false}
            value={primaryContact.value}
            onChange={(field, value) =>
              setPrimary((oldValues) => ({
                ...oldValues,
                value: value,
                new: users.find((user) => user.id === value),
              }))
            }
            options={users}
            allowClear={false}
            size={Size.FULL_WIDTH}
            className="required"
          />
          <SelectInput
            field="alternativeContact"
            label="Alternative Contact:"
            multiSelect={false}
            value={alternativeContact.value}
            onChange={(field, value) =>
              setAlternative((oldValues) => ({
                ...oldValues,
                value: value,
                new: users.find((user) => user.id === value),
              }))
            }
            options={users}
            allowClear={false}
            size={Size.FULL_WIDTH}
          />
        </section>
      )}
      {error && <div className="form-error">{error}</div>}
    </PopupWindow>
  );
};

Company.propTypes = propTypes;

export default Company;
